import { AuthenticationOptions } from "api/Auth";
import LibUtils from "lib/LibUtils";
import {
    AccountLoginResult,
    AccountType, ApprovalStatus,
    AwardType, BadgeRuleAction,
    BadgeRuleFrequence, BadgeRulePeriod,
    CourseItemStatus, DynamicSystemRoles, FeedType,
    GroupType,
    IntegrationQueueEvent, JobLogEventType, JobLogStatus, KnowledgeTestDifficultyLevel,
    KnowledgeTestImportance, KnowledgeTestSelectionMode,
    KnowledgeTestStatus, KnowledgeTestTrustLevel, ListType,
    MediaServicesProcessingStatus, MemberType, NotificationEmailOption,
    NotificationMainGroupType, NotificationSubGroupType, NotificationType, ParseFileType,
    PublicationActionStatus, PublicationActionType,
    PublicationCognitiveAnalysisReason, PublicationEditPermissions, PublicationLikeOption,
    PublicationShareType, PublicationType,
    PublicationViewPriority,
    PublicationVisility,
    QueueEventType, QueueViewStatus, RelationshipCreationMode, SystemRoles,
    TenantBillingType, TopicCommonUserApprovalMethod,
    TopicDashboardKnowledgeDatabaseSummaryColumn,
    TopicDashboardResultsSummaryColumn, TopicWhoAnalyzesApproval, TopicWhoDetermineSpecialist,
    UserCredentialType,
    UserKnowsAboutSpecialistGrantMethod, UserPhoneType
} from "./ApiEnums";


//#region API

export interface ApiExceptionDetailedError {
    errorId?: string;
    errorNum?: number;
    message: string;
    extendedParameters: string;
    severity: number;
}

export interface ApiException {
    exceptionMessage: string;
    exceptionType: string;
    exceptionCode: number;
    stackTrace: string;
    errorList: ApiExceptionDetailedError[];
    traceIdentifier?: string;
    isFatal: boolean;
}

export interface PagedResult<T> {
    items: Array<T>;
    total: number;
}

export interface LogRecord {
    creationDate: Date;
    creator: UserShortView;
    jsonData: string;
}

export interface RecordInfo {
    creator: UserShortView;
    revisor: UserShortView;

    creationDate: Date;
    lastUpdateDate: Date;
}

export interface GenericListItemDto {
    id: string;
    name: string;
    thumbnailUrl: string;
}

export interface SearchPublicationsExcludeOptions {
    excludePublications?: string[];
    excludePublicationsType?: PublicationType[];
    excludePublicationsFromListId?: string;
    excludePublicationsFromCourseId?: string;
}

export interface SubtitleItemDto {
    startTime: number;
    timeRange: string;
    lines: string;
}

export interface WordItemDto {
    word: string;
    indexes: number[];
}

export interface AwardResult {
    awardType: AwardType;
    points: number;
}

export interface TimezoneRecordDto {
    zoneId: string;
    name: string;
}

export interface AboutInfoDto {
    version: string;
    copyright: string;
    product: string;
    company: string;
}

export interface ReleaseNoteDto {
    releaseNumber: string;
    releaseDate: Date;
}

//#endregion

//#region Upload/Download

export interface MediaAssetModel {
    encodedAssetId: string;
    encodedUrls: string;
    smoothStreamingUri: string;
    hlsUri: string;
    mpegDashUri: string;
    progressiveDownloadUris: string[];
    posterUrl: string;
    hasSubtitle: boolean;
}

export interface StoreMetadataModel {
    title: string;
    description: string;
    author: string;
    fileCreationDate?: Date;
    fileExtension?: string;
    fileSize?: number;
    filename?: string;
    width?: number;
    height?: number;
    duration?: number;
}

export interface AsyncUploadModel {
    uploadId: string;
    success: boolean;
    mainFile: FileUploadResultDto;
    thumbnail: FileUploadResultDto;
}

export interface FilePostDto {
    fileId: string;
}

export interface FileGetDto {
    fileId: string;
    url?: string;
    mimeType?: string;
    metadata?: StoreMetadataModel;
    mediaAsset?: MediaAssetModel;
    isEmbed?: boolean;
}

export interface FileThumbnailPostDto {
    fileId: string;
    thumbnailId: string;
}

export interface FileThumbnaildGetDto {
    mainFile: FileGetDto;
    thumbnail: FileGetDto;
    resizedImage: FileGetDto;
}

export interface FileUploadResultDto {
    fileId: string;
    mimeType: string;
    tempFilename: string;
    tempUrl: string;
    attachUrl: string;
    metadata: ParsedFileResult;
}

export interface ParsedFileResult {
    fileType: ParseFileType;
    filename: string;
    fileExtension: string;
    fileSize?: number;
    fileCreationDate?: Date;
    title: string;
    description: string;
    width: number;
    height: number;
    duration?: number;
    containsImageOnly: boolean;
    keywords: string[];
    author: string;
    language: string;
    metadata: string;
}


export interface ContentMetadata {
    uId?: string;

    provider: string;
    title: string;
    type?: string;
    siteName?: string;
    description?: string;

    url?: string;
    image?: string;
    width?: number;
    height?: number;

    authorName?: string;
    authorUrl?: string;
    thumbnailWidth?: number;
    thumbnailHeight?: number;
    tumbnailUrl?: string;
    embedHtml?: string;
    resizedThumbnailId?: string;
    resizedThumbnailUrl?: string;

    creationDate?: Date;
}

export interface ContentMetadataGetDto extends ContentMetadata {
    publicationId?: string;
    hasSubtitles?: boolean;
}

export interface SummaryMetadata {
    content?: string;
}

//#endregion

//#region SEARCH

export interface GlobalSearchItem {
    id: string;
    parentId: string;
    title: string;
    subtitle: string;
    additionalInfo: string | FileGetDto;
    authorId: string;
    authorName: string;
    publicationType?: PublicationType;
    thumbnailUrl: string;
    isSpecialist?: boolean;
    enrolled?: boolean;
    lastUpdateDate?: Date;
    totalViews: number;
    totalPublications: number;
    isInstitutional?: boolean;
}

export interface GlobalSearchRecord {
    items: GlobalSearchItem[];
    totalRecords: number;
}

export interface GlobalSearchResult {
    users: GlobalSearchRecord;
    topics: GlobalSearchRecord;
    courses: GlobalSearchRecord;
    documents: GlobalSearchRecord;
    publications: GlobalSearchRecord;
}

export enum GlobalSearchPublicationOrderBy { MostRelevant, MostRecent, MostViewed, MostLiked }
export enum GlobalSearchPublicationPeriod { Any, Today, LastWeek, LastMonth, LastQuarter, LastYear, Custom }
export enum GlobalSearchContent { All, Video, Image, File, WithAnswer }
export enum GlobalSearchDocumentOrderBy { MostRelevant, LastUpdateDate, Title }

export interface GlobalPublicationSearchFilter {
    searchText: string;
    period?: GlobalSearchPublicationPeriod;
    periodCustomStart?: Date;
    periodCustomEnd?: Date;
    publicationType?: PublicationType;
    content?: GlobalSearchContent;
    specialistOnly?: boolean;
}

export interface GlobalDocumentSearchFilter {
    searchText: string;
    publicationType?: PublicationType;
}

//#endregion

//#region AZURE SEARCH

export interface ImageSearchFilter {
    searchTerm: string;
}


export interface AzureSearchImageItem {
    contentSize: string;
    contentUrl: string;
    datePublished: string;
    description: string;
    encodingFormat: string;
    height: number;
    name: string;
    thumbnailUrl: string;
    width: number;
}

export interface AzureSearchImageResult {
    total: number;
    count: number;
    nextOffset: number;
    items: AzureSearchImageItem[];
}

//#endregion

//#region NOTIFICATION

export interface NotificationCalloutDto {
    feed: FeedType;
    topicId?: string;
    callout: boolean;
}

export class NotificationDto {
    notificationId: string;
    day: Date;

    mainGroupType: NotificationMainGroupType;
    subGroupType?: NotificationSubGroupType;
    notificationType: NotificationType;
    notificationData: JSON;
    creationDate: Date;
    counter: number;
    userCounter: number;
    message: string;

    targetPublicationId?: string;
    targetPublicationType?: PublicationType;
    targetPublicationThumbnailUrl?: string;
    targetPublicationTitle?: string;
    targetPublicationIsPublic?: boolean;

    thumbnailUrl?: string;
    thumbnailTitle?: string;

    relatedUserId: string;
    relatedUserName: string;
    relatedUserThumbnailUrl: string;
    relatedOldObjectName: string;

    relatedTopicId: string;
    relatedTopicName: string;
    relatedTopicThumbnailUrl: string;

    relatedAnswerId: string;

    relatedCommentId: string;

    containNewNotification: boolean;

    followingPublication: boolean;

    groupId: string;
}

export interface NotificationOptionDto {
    blockNotification?: boolean;
    emailOption?: NotificationEmailOption;
}

export interface NotificationSetupItemDto {
    subGroupType?: NotificationSubGroupType;
    notificationType?: NotificationType;
    options: NotificationOptionDto;
}

export interface NotificationSetupMainGroupDto {
    mainGroupType: NotificationMainGroupType;
    items: NotificationSetupItemDto[];
}

export interface NotificationSetupDto {
    mainGroups: NotificationSetupMainGroupDto[];
}

//#endregion

//#region Session

export interface QrCodeDto {
    qrCodeToken: string;
    qrAlternateAccessCode: string;
    qrCodeExpires: Date;
}

export interface UserSessionInfo {
    id: string;
    name: string;
    culture: string;
    timezoneId: string;
    thumbnailUrl: string;
    dynamicRoles?: DynamicSystemRoles;
    roles: SystemRoles;
    lastUpdate: number;
    keepConnected: boolean;
    accountType: AccountType;
    acceptedCookies: boolean;
}

export interface JwtToken {
    token: string;
    tokenValidTo: Date;
    refreshToken: RefreshToken;
    sessionId: string;
}

export interface JsonWebToken extends JwtToken {
    userSessionInfo: UserSessionInfo;
}

export interface RefreshToken {
    token: string;
    expirationDate: Date;
}

export interface AuthenticationResult {
    status: AccountLoginResult;
    webToken: JsonWebToken;
    continueAuthStamp: string;
}

//#endregion

/*****************************************************
 * ADMINISTRATION
 ****************************************************/

//#region QUEUE

export interface QueueEventViewModel {
    queueEventType: QueueEventType;
    tenantId: string;
    id: string;
    postedOn: Date;
    recipientId: string;
    recipientName: string;
    recipientEmail: string;
    subject: string;
    retries?: number;
    lastProcessingDate?: Date;
    lastStatusCode?: number;
    lastStatus: string;
    status: QueueViewStatus;
}

export interface QueueFilter {
    userId?: string;
    subject?: string;
    status?: QueueViewStatus;
    queueEventType?: QueueEventType;
    startDate?: Date;
    endDate?: Date;
}

export interface QueueViewModelParams {
    filter: QueueFilter;
    range: PageRange;
}

//#endregion

//#region JOB LOG

export interface JobLogEventTypeDto {
    eventId: number;
    eventName: string;
}

export interface JobLogDto {
    id: string;
    eventId: number;
    eventName: string;
    status: JobLogStatus;
    startedAt: Date;
    finishedAt?: Date;
    additionalInfo: JSON;
    timeSpent?: number;
}

export enum JobLogFilterPeriod { All = 0, Today = 1, LastWeek = 2, LastTwoWeeks = 3 }

export interface JobLogFilter {
    period: JobLogFilterPeriod;
    status: JobLogStatus;
    eventId?: number;
    eventType: JobLogEventType;
}

export interface JobLogParams {
    filter: JobLogFilter;
    range: PageRange;
}

//#endregion

//#region Member

export interface MemberDto {
    memberId: string;
    memberType: MemberType;
}

export interface MemberShortViewModel {
    memberId: string;
    name: string;
    displayName: string;
    thumbnailUrl: string;
    isActive: boolean;
    memberType: MemberType;
    roles: SystemRoles;
}

export interface MemberSuggestionDto extends AutoCompleteDataModel {
    memberType: MemberType;
    displayName?: string;
    isActive?: boolean;
    roles?: SystemRoles;
}

//#endregion

//#region User

export interface SavePassportResult {
    award: AwardResult;
    account: GetUserAccountDto;
    insertedWithoutLicense: boolean;
}

export interface UserRecordFormConfigDto {
    chooseLocalFromList: boolean;
    editLocalInUserProfile: boolean;
    localName: string;
    timezones: TimezoneRecordDto[];
    cultures: KeyValueItem<string, string>[];
    blockCodes: KeyValueItem<string, { description: string; externalCode: string; isSystem: boolean; }>[];
    authOptions: AuthenticationOptions;
    corporateMode: boolean;
    hasLicenceAvailable?: boolean;
    maxUsers?: number;
}

export interface UserEntityModel {
    userId: string;
    name: string;
    thumbnailUrl?: string;
    isSystemUser?: boolean;
}

export interface UserShortView {
    id: string;
    name?: string;
    email?: string;
    culture?: string;
    thumbnailUrl?: string;
    isSystemUser?: boolean;
}

export interface UserPhoneModel {
    type: UserPhoneType;
    index: number;
    number: string;
    visibleToOthers: boolean;
    isValid?: boolean;
}

export interface UserSocialNetworkModel {
    networkName: string;
    value: string;
}

export interface UserCredentialsModel {
    index: number;
    type: UserCredentialType;
    description: string;
    startDate: Date;
    endDate: Date;
}

export interface UserStatisticsDto {
    posts: number;
    questions: number;
    lists: number;
    courses: number;
    polls: number;
    surveys: number;
    answers: number;

    viewPublications: number;
    publicationsViewed: number;

    likes: number;
    dislikes: number;
    likesBalance: number;

    likesReceived: number;
    dislikesReceived: number;
    likesReceivedBalance: number;

    thanks: number;
    thanksReceived: number;

    following: number;
    followers: number;

    awardsMonth: number;
    awardsTotal: number;

    totalPublications: number;
}


export interface UserRecordCounters {
    publications: number;
    followers: number;
    logEntries: number;
}

export interface UserEndorsementGetDto {
    endorser: UserShortView;
}

export interface UserKnowsAboutSpecialistDto {
    grantedBy?: string;
    specialistGrantMethod: UserKnowsAboutSpecialistGrantMethod;
}

export interface UserKnowsAboutGetDto {
    topicId: string;
    topicName: string;
    topicAllowUserSpecialist: boolean;
    topicThumbnailUrl: string;

    specialist: UserKnowsAboutSpecialistDto;

    currentEndorser: UserEndorsementGetDto;
    endorsementsCount: number;
    endorserHandleSpecialist: boolean;
}

export interface UserKnowledgeTopicGetDto {
    topicId: string;
    name: string;
    thumbnailUrl: string;
    following: boolean;
    fromGroupName: string;
    toBeFollowed?: boolean;
}


export interface UserFollowTopicGetDto {
    topicId: string;
    name: string;
    thumbnailUrl: string;
}

export interface UserFollowUserGetDto {
    userId: string;
    name: string;
    thumbnailUrl: string;
}

export interface UserBannedPersonDto {
    blockedUser: UserShortView;
    blockedOn: Date;
}

export interface UserKnowsAboutPostDto {
    topicId: string;
    managerEndorsementOption?: boolean;
    managerSpecialistOption?: boolean;
}

export interface UserKnowledgeTopicPostDto {
    topicId: string;
    toBeFollowed: boolean;
}

export interface BaseGetUserDto {
    userId: string;
    name: string;
    email: string;
    culture: string;
    timezoneId: string;
    thumbnail: FileGetDto;
    locationId: string;
    locationName: string;
    recordInfo: RecordInfo;
    deleted: boolean;
    blocked: boolean;
    canChangeBlockCode?: boolean;
}

export interface BasePostUserDto {
    userId: string;
    name: string;
    email: string;
    culture: string;
    timezoneId: string;
    thumbnail: FilePostDto;
}

export interface UserAccountFieldsDto {
    passwordExpirationDate?: Date;
    lastPasswordChangeDate?: Date;
    accountExpirationDate?: Date;
    accountConfirmationDate?: Date;
    lastLogin?: Date;
    isLockedOut: boolean;
    accountType: AccountType;
}

export interface UserPersonalFieldsDto {
    credentials?: UserCredentialsModel[];
    phones: UserPhoneModel[];
    socialNetworks: UserSocialNetworkModel[];
}


export interface UserCorpFieldsDto {
    manager: UserShortView;
    admissionDate?: Date;
    resignationDate?: Date;
}


export interface UserBadgeDto extends BadgeDto {
    wonLastDate?: Date;
    wonTotalPoints?: number;
    wonTimes?: number;
}


export interface GetUserProfileDto extends BaseGetUserDto {
    badges: BadgeDto[];
    personal: UserPersonalFieldsDto;
    statistics: UserStatisticsDto;
    currentUserRelation: UserProfileRelationDto;
    followersCount: number;
}

export interface UserProfileRelationDto {
    following: boolean;
    notifyChanges: boolean;
    muted: boolean;
    blocked: boolean;
    blockedByProfile: boolean;
}

export interface UserTopicInterationDto {
    following: boolean;
    notifyChanges: boolean;
    inBlackList: boolean;
}

export interface GetUserAccountDto extends BaseGetUserDto {
    knowsAbout: UserKnowsAboutGetDto[];
    personal: UserPersonalFieldsDto;
    account: UserAccountFieldsDto;
    followingTopics: UserFollowTopicGetDto[];
    followingUsers: UserFollowUserGetDto[];
    bannedPeople: UserBannedPersonDto[];
}

export interface GetUserDto extends BaseGetUserDto {
    blockCodeId: string;
    ownRoles: number;
    groupsRoles: number;
    groups: GroupShortView[];
    knowsAbout: UserKnowsAboutGetDto[];
    knowledgeTopics: UserKnowledgeTopicGetDto[];

    account: UserAccountFieldsDto;
    corp: UserCorpFieldsDto;
    counters: UserRecordCounters;
    personal: UserPersonalFieldsDto;
}

export interface GetEditUserDto extends GetUserDto {
    generateAndSentPassword: boolean;
    newPassword: string;
    confirmPassword: string;
}

export interface SaveUserRegisterDto extends BasePostUserDto {
    password: string;
}

export interface SaveAccountDto extends BasePostUserDto {
    locationId: string;
    locationName: string;

    personal: UserPersonalFieldsDto;
    knowsAbout: UserKnowsAboutPostDto[];

    followTopics: string[];
    followUsers: string[];
    bannedPeople: string[];
}

export interface SaveUserDto extends BasePostUserDto {
    locationId: string;
    locationName: string;

    blockCodeId: string;
    groups: string[];
    ownRoles: SystemRoles;
    knowsAbout: UserKnowsAboutPostDto[];

    generateAndSentPassword: boolean;
    newPassword: string;

    account: {
        accountExpirationDate: Date;
        isLockedOut: boolean;
    },

    corp: {
        managerId: string;
        admissionDate: Date;
        resignationDate: Date;
    },

    personal: {
        credentials: UserCredentialsModel[];
        phones: UserPhoneModel[];
        socialNetworks: UserSocialNetworkModel[];
    },
}

export interface UserFollowingAssetLookup {
    id: string;
    name: string;
    thumbnailUrl: string;
}

export interface UserSidebarFollowingAssetLookup extends UserFollowingAssetLookup {
    fixed: boolean;
}

export interface UserAdminListDto {
    userId: string;
    name: string;
    email: string;
    thumbnailUrl: string;
    blockReason: string;
    isLockedOut: boolean;
    creationDate: Date;
    deleted: boolean;
    accountType: AccountType;
}


export interface ImportUsersLineResultDto {
    line: string;
    message: string;
}

export interface ImportUsersResultDto {
    successCount: number;
    licensedBlocked: number;
    errors: ImportUsersLineResultDto[];
}

export interface ManagerTreeNode {
    userId: string;
    parentId?: string;
    name: string;
    thumbnailUrl: string;
    children: ManagerTreeNode[];
}

//#endregion

//#region Group

export interface GroupRecordCounters {
    members: number;
    logEntries: number;
}


export interface GroupItemChangedDto {
    id: string;
    operation: "add" | "delete";
}


export interface GetGroupDto {
    groupId: string;
    name: string;
    description: string;
    thumbnail: FileGetDto;
    recordInfo: RecordInfo;
    counters: GroupRecordCounters;
    ownRoles: SystemRoles;
    groupsRoles: SystemRoles;
    changedMembers: GroupItemChangedDto[];
    changedSuggestedTopics: GroupItemChangedDto[];
    groupType: GroupType;
}

export interface SaveGroupDto {
    groupId: string;
    name: string;
    description: string;
    thumbnail: FilePostDto;
    ownRoles: SystemRoles;
    changedMembers: GroupItemChangedDto[];
    changedSuggestedTopics: GroupItemChangedDto[];
}


export interface GroupShortView {
    id: string;
    name: string;
    thumbnailUrl?: string;
    inherited?: boolean;
    roles?: SystemRoles;
    groupType: GroupType;
}

export interface SelectableGroupShortView extends GroupShortView {
    checked: boolean;
}


export interface GroupSuggestionDto extends AutoCompleteDataModel {
    roles?: SystemRoles;
    groupType: GroupType;
}


export interface GroupAdminListDto {
    groupId: string;
    name: string;
    thumbnailUrl: string;
    isActive: boolean;
    creationDate: Date;
    lastUpdateDate: Date;
    groupType: GroupType;
    members: number;
}

export enum GroupListOrderBy { Name, LastUpdateDate }

export interface GroupListFilter extends FeedBaseFilter {
    isActive?: boolean;
    isInactive?: boolean;
}

export interface GetGroupListParams {
    filter: GroupListFilter;
    orderBy: GroupListOrderBy;
    range: PageRange;
}


//#endregion

//#region TOPIC

export interface TopicRecordFormConfigDto {
    specialistMinLikes: number;
    specialistMinPubs: number;
    specialistMinThanks: number;
    approverOnlySuspectPublication: boolean;
    mediaModerationEnabled: boolean;
}


export interface TopicSuggestionDto extends AutoCompleteDataModel {
    displayName?: string;
    hasMemberRestriction?: boolean;
    allowUserSpecialist?: boolean;
    currentUserDetermineSpecialist?: boolean;
}

export interface TopicPredictionsDto {
    topics: TopicSuggestionDto[];
    relativesTopics: TopicSuggestionDto[];
}

export interface TopicRelationship {
    topicId: string;
    name: string;
}

export interface TopicMembersOperationDto {
    topicId: string;
    members: MemberDto[];
    message: string;
}


export interface TopicRecordCounters {
    publications: number;
    followers: number;
    members: number;
}

export interface GetTopicDto {
    topicId?: string;
    name: string;
    description: string;
    thumbnail: FileGetDto;

    aka: string[];
    isSubtopicOf: KeyValueItem<string, string>[];
    containsSubtopics: KeyValueItem<string, string>[];
    specialists: TopicSpecialistDto[];

    approver: UserShortView;
    whoAnalyzesApproval: TopicWhoAnalyzesApproval;
    commonUserApprovalMethod: TopicCommonUserApprovalMethod;

    whoDetermineSpecialist: TopicWhoDetermineSpecialist;
    specialistRequireLevelNinja: boolean;
    specialistRequireMinEndorssement: boolean;
    specialistRequireMinPublications: boolean;
    specialistRequireManagerEndorssement: boolean;
    allowUserSpecialist: boolean;

    changedAdministrators: GroupItemChangedDto[];
    changedMembers: GroupItemChangedDto[];

    isPublic: boolean;
    applyHierarchyTopicsOnCreatePub: boolean;

    editInDelegationMode: boolean;
    recordInfo: RecordInfo;

    counters: TopicRecordCounters;
}

export interface SaveTopicDto {
    topicId?: string;
    name: string;
    description: string;
    thumbnail: FilePostDto;

    aka: string[];
    isSubtopicOf: string[];
    containsSubtopic: string[];

    approverId: string;

    whoAnalyzesApproval: TopicWhoAnalyzesApproval;
    commonUserApprovalMethod: TopicCommonUserApprovalMethod;

    whoDetermineSpecialist: TopicWhoDetermineSpecialist;
    specialistRequireLevelNinja: boolean;
    specialistRequireMinEndorssement: boolean;
    specialistRequireMinPublications: boolean;
    allowUserSpecialist: boolean;
    isPublic: boolean;
    applyHierarchyTopicsOnCreatePub: boolean;
    applySpecialistGroupOnSubtopics: boolean;
    applyApprovalAnalyseGroupOnSubtopics: boolean;

    changedAdminstrators: GroupItemChangedDto[];
    changedMembers: GroupItemChangedDto[];
}


export interface TopicShortViewModel {
    topicId: string;
    name: string;
    thumbnailUrl: string;
}

export interface TopicListAdminItemDto {
    topicId: string;
    name: string;
    description: string;
    thumbnailUrl: string;
    creationDate: Date;

    followers: number;
    publications: number;
    courses: number;
    whoKnowsAbout: number;
    knowledgeTests: boolean;
    hasDescendents: boolean;
}

export interface TopicUserFlowListItemDto extends TopicListAdminItemDto {
    following: boolean;
}

export interface TopicSpecialistDto extends UserKnowsAboutSpecialistDto {
    userId: string;
    name: string;
    thumbnailUrl: string;
}

export interface TopicDashboardKnowledgeDatabaseParams {
    topicId: string;
    columnToSummary: TopicDashboardKnowledgeDatabaseSummaryColumn;
    searchText?: string;
    range?: PageRange;
}

export interface TopicDashboardResultsParams {
    topicId: string;
    columnToSummary: TopicDashboardResultsSummaryColumn;
    searchText?: string;
    range?: PageRange;
}

export interface TopicDashboardKnowledgeDatabaseModel {
    totalTests: number;
    totalTestsHighPriority: number;
    totalFutureTests: number;
    indexTestBellow50: number;

    levelBeginner: number;
    levelIntermediate: number;
    levelAdvanced: number;

    recBeginner: number;
    recIntermediate: number;
    recAdvanced: number;
}

export interface TopicDashboardResultsModel {
    totalUsersInTopic: number;
    totalAnswers: number;
    indexCorrect: number;
    indexUserBellow50: number;
    indexParticipation: number;

    levelNotInitiate: number;
    levelBeginner: number;
    levelIntermediate: number;
    levelAdvanced: number;
    levelNinja: number;
}

export interface TopicWhoKnowsAboutDto extends UserKnowsAboutSpecialistDto {
    userId: string;
    name: string;
    thumbnailUrl: string;
    endorsementsCount: number;
    currentUserEndorsed: boolean;
}

export interface TopicStatisticsDto {
    topicId: string;

    publications: number;
    publicationsActive: number;
    publicationsInstitutional: number;
    highlights: number;

    authors: number;
    posts: number;
    lists: number;
    polls: number;
    courses: number;
    questions: number;
    answeredQuestions: number;
    notAnsweredQuestions: number;

    likes: number;
    thanks: number;
    views: number;
    comments: number;

    followers: number;
    knowledgers: number;
    specialists: number;
    members: number;
    tests: number;
}

export interface TopicRelationshipDto {
    topicId: string;
    name: string;
    creationMode: RelationshipCreationMode;
}

export interface TopicGraphNodeDto {
    id: string;
    label: string;
    color?: string;
    isPublic: boolean;
    hasPublication: boolean;
    title?: string;
}

export interface TopicGraphLinkDto {
    from: string;
    to: string;
    weight?: number;
}

export interface TopicGraphDto {
    nodes: TopicGraphNodeDto[];
    edges: TopicGraphLinkDto[];
}

export interface TopicTreeItemDto {
    topicId: string;
    parentId: string;
    name: string;
    isPublic: boolean;
    hasPublication: boolean;
}

//#endregion

//#region Gamification

export interface BadgeAdminListDto {
    badgeId: string;
    name: string;
    description: string;
    thumbnailUrl: string;
    points: number;
    isActive: boolean;
    expired: boolean;
    notStarted: boolean;
    availableStartDate?: Date,
    availableEndDate?: Date,
    creationDate: Date;
    LastUpdateDate: Date;
    wonCount: number;
}

export interface BadgeRecordCounters {
    won: number;
    uniqueWon: number;
    logEntries: number;
    lastWon: Date;
}

export interface GetBadgeDto {
    badgeId: string;
    description: string;
    isActive: boolean;
    name: string;
    points: number;
    thumbnail: FileGetDto;

    availableStartDate: Date,
    availableEndDate: Date,

    applyRuleInValidPeriodOnly: boolean;
    ruleAction: BadgeRuleAction;
    ruleHowMany?: number;
    rulePeriod?: BadgeRulePeriod;
    ruleFrequence?: BadgeRuleFrequence;
    ruleMaxUsers?: number;
    ruleMaxUsersPerPeriod?: boolean;
    ruleTopicId?: TopicSuggestionDto;
    ruleCourses?: PublicationSuggestionDto[];

    recordInfo: RecordInfo;
    counters: BadgeRecordCounters;
}

export interface SaveBadgeDto {
    badgeId: string;

    availableStartDate: Date,
    availableEndDate: Date,

    description: string;
    name: string;
    isActive: boolean;
    points: number;
    thumbnail: FilePostDto;

    applyRuleInValidPeriodOnly: boolean;
    ruleAction: BadgeRuleAction;
    ruleHowMany?: number;
    rulePeriod?: BadgeRulePeriod;
    ruleFrequence?: number;
    ruleMaxUsers?: number;
    ruleMaxUsersPerPeriod?: boolean;
    ruleTopicId?: string;
    ruleCourses?: string[];
}

export interface BadgeDto {
    badgeId: string;
    name: string;
    description: string;
    thumbnailUrl: string;
    isActive: boolean;
    points: number;
}


export interface BadgeListFilter extends FeedBaseFilter {
    isActive: boolean;
}

export enum BadgeListOrderBy { Name, MostWon, MostPoints }

export interface GetBadgeListParams {
    filter: BadgeListFilter;
    orderBy: BadgeListOrderBy;
    range: PageRange;
}

export interface GetAwardDto {
    tenantId: string;
    grantPostPoints?: number;
    grantQuestionPoints?: number;
    grantProfileCredentialPoints?: number;
    quizNewSessionMaxPoints: number;
    quizRevisionMaxPoints: number;
    courseCompletePoints: number;
    recordInfo: RecordInfo;
    counters: AwardRecordCounters;
}

interface AwardRecordCounters {
    logEntries: number;
    topicExceptions: number;
}

export interface SaveAwardDto {
    grantPostPoints?: number;
    grantQuestionPoints?: number;
    grantProfileCredentialPoints?: number;
    updateHistoryPostPoints: boolean;
    updateHistoryQuestionPoints: boolean;
    quizNewSessionMaxPoints: number;
    quizRevisionMaxPoints: number;
    courseCompletePoints: number;
}

export interface GetAwardTopicExceptionDto {
    topicId: string;
    name: string;
    quizNewSessionMaxPoints: number;
    quizRevisionMaxPoints: number;
}

export interface SaveAwardTopicExceptionDto {
    topicId: string;
    quizNewSessionMaxPoints: number;
    quizRevisionMaxPoints: number;
}


export interface AwardRankingListResult {
    userRank: RankingDto;
    rankingList: PagedResult<RankingDto>;
}

export interface RankingDto {
    yearMonth: number;
    position: number;
    points: number;
    userId: string;
    name: string;
    thumbnailUrl: string;
}


export interface AwardHistoryDto {
    awardType: AwardType;
    creationDate: Date;
    points: number;

    badgeId: string;
    badgeName: string;

    publicationId: string;
    publicationType: PublicationType;
    publicationTitle: string;
    questionId?: string;

    topicId: string;
    topicName: string;
}

export interface AwardHistoryGraphDto {
    yearMonth: number;
    points: number;
}

//#endregion

//#region QUIZ

export interface QuizTopicDiscoveryModel {
    topicId: string;
    name: string;
    userCurrentLevel: KnowledgeTestDifficultyLevel;
    sequential: boolean;

    questionsBySession: number;

    newSessionMaxPoints: number;
    revisionMaxPoints: number;

    answeredTests: number;
    newestCounter: number;
    newestMinDifficultyLevel: KnowledgeTestDifficultyLevel;
    wrongCounter: number;
    wrongMinDifficultyLevel: KnowledgeTestDifficultyLevel;

    revision: boolean;
    revisionAnsweredTests: number;

    computedMaxPoints: number;
    computedTestsToAnswer: number;
    computedAwardPoints: number;
}

export interface QuizSessionInfoDto {
    sessionId: string;
    revision: boolean;
    topicId: string;
    topicName: string;
    topicThumbnailUrl: string;
}
export interface QuizSessionDto extends QuizSessionInfoDto {
    maxItems: number;
    firstItem: QuizItemDto;
    result: QuizSessionResultModel;
}

export interface QuizItemDto {
    testId: string;
    currentIndex: number;
    title: string;
    attach: FileThumbnaildGetDto;
    answerSelectionMode: KnowledgeTestSelectionMode;
    alternatives: QuizAlternativeDto[];
}

export interface QuizAlternativeDto {
    alternativeId: string;
    text: string;
}

export interface QuizSessionSaveAnswerDto {
    sessionId: string;
    testId: string;
    alternatives: string[];
    trustLevel: KnowledgeTestTrustLevel;
}


export interface QuizAlternativeResultDto {
    alternativeId: string;
    shouldBeSelected: boolean;
    correct: boolean;
    selected: boolean;
    feedback: string;
    showCorrectAnswer: boolean;
}

export interface QuizAnswerResultDto {
    correct: boolean;
    alternatives: QuizAlternativeResultDto[];
}

export interface QuizSessionResultModel {
    totalAnswers: number;
    totalCorrect: number;
    awardPoints: number;
    knowledgePercent: number;
    completePercent: number;
    currentLevel: KnowledgeTestDifficultyLevel;
}

export interface QuizSaveResultDto {
    answerResult: QuizAnswerResultDto;
    maxItems: number;
    nextItem: QuizItemDto;
    performance: QuizSessionResultModel;
    award: AwardResult;
}

//#endregion

//#region Knowledge Test

export interface KnowledgeTestTopicItemDto {
    topicId: string;
    name: string;
    thumbnailUrl: string;
    numberOfKnowledgeTests: number;
    numberOfRequiredMembers: number;
}


export interface KnowledgeTestAdminItemDto {
    testId: string;
    title: string;
    status: KnowledgeTestStatus;
    answerSelectionMode: KnowledgeTestSelectionMode;
    difficultyLevel: KnowledgeTestDifficultyLevel;
    importance: KnowledgeTestImportance;
    applyTestStartDate: Date;
    applyTestEndDate: Date;
    expired: boolean;

    topics: TopicSuggestionDto[];

    creator: UserShortView;
    creationDate: Date;

    revisor: UserShortView;
    lastUpdateDate: Date;

    stats: {
        answers: number;
        correctAnswers: number;
    };

    permissions: {
        canEdit: boolean;
        canDelete: boolean;
    };
}

export interface GetKwownledgeTestDto {
    testId: string;
    title: string;
    answerSelectionMode: KnowledgeTestSelectionMode;
    difficultyLevel: KnowledgeTestDifficultyLevel;
    importance: KnowledgeTestImportance;
    applyTestStartDate?: Date;
    applyTestEndDate?: Date;

    alternatives: KnowledgeTestAlternativeGetDto[];
    topics: TopicSuggestionDto[];
    attach: FileThumbnaildGetDto;

    active: boolean;
    blocked: boolean;
    hasAnswers: boolean;
    editPermissions: PublicationEditPermissions;

    defaultRecommendations: PublicationSuggestionDto[];
    defaultFeedback: string;
}

export interface SaveKnowledgeTestDto {
    testId: string;
    title: string;
    answerSelectionMode: KnowledgeTestSelectionMode;
    difficultyLevel: KnowledgeTestDifficultyLevel;
    importance: KnowledgeTestImportance;
    applyTestStartDate?: Date;
    applyTestEndDate?: Date;

    alternatives: KnowledgeTestAlternativeSaveDto[];
    topics: string[];
    attach?: FileThumbnailPostDto;

    defaultRecommendations: string[];
    defaultFeedback: string;
}

export interface KnowledgeTestAlternativeGetDto {
    id: string;
    text: string;
    shouldBeSelected: boolean;
    feedback: string;
    feedback2: string;
    recommendations: PublicationSuggestionDto[];
    useDefaultFeedback: boolean;
}

export interface KnowledgeTestAlternativeSaveDto {
    id: string;
    text: string;
    shouldBeSelected: boolean;
    feedback: string;
    feedback2: string;
    recommendations: string[];
    useDefaultFeedback: boolean;
}

export interface KnowledgeTestTopicSuggestionDto extends AutoCompleteDataModel {
    following: boolean;
    fromGroupName: string;
}


export interface KnowledgeTestWhoGotRightDto extends GenericListItemDto {
    totalCorrect: number;
    totalAnswers: number;
}

export interface KnowledgeTestAnswersDistributionModel {
    alternativeId: string;
    text: string;
    shouldBeSelected: boolean;
    answerSelectionMode: KnowledgeTestSelectionMode;
    totalCorrect: number;
    totalAnswers: number;
}

export interface KnowledgeTestResultAnalysisModel {
    totalAnswers: number;
    trustLow: number;
    trustMedium: number;
    trustHigh: number;
    trustHighWrong: number;
    trustHighCorrect: number;
    trustLowCorrect: number;
    retentionAnswers: number;
    retentionCorrect: number;
}

//#endregion

//#region SETUP

export interface GetAgreementDto {
    text: string;
    file: FileGetDto;
}

export class GetSetupAgreementDto {
    text: string;
    file: FileThumbnaildGetDto;
    recordInfo: RecordInfo;
}

export interface SaveSetupAgreementDto {
    text: string;
    file: FilePostDto;
}

export class GetSetupAdminDto {
    title: string;
    mainTheme: string;
    minPasswordLen: number;
    passwordHasOneNumber: boolean;
    passwordHasOneUpperLetter: boolean;
    passwordHasOneSymbol: boolean;
    passwordExcludeNameSurname: boolean;
    maxAuditLogDays: number;

    enableAutoRegister: boolean;
    enablePasswordRecovery: boolean;
    requireAccountConfirmation: boolean;
    corporateMode: boolean;

    maxInvalidPasswordAttempts: number;
    passwordAttemptWindow: number;

    emailDefaultAddress: string;
    emailDefaultName: string;

    recordInfo: RecordInfo;
    logo: FileGetDto;
    background: FileGetDto;
}

export interface SaveSetupAdminDto {
    title: string;
    mainTheme: string;
    minPasswordLen: number;
    passwordHasOneNumber: boolean;
    passwordHasOneUpperLetter: boolean;
    passwordHasOneSymbol: boolean;
    passwordExcludeNameSurname: boolean;
    maxInvalidPasswordAttempts: number;
    passwordAttemptWindow: number;

    maxAuditLogDays: number;

    enableAutoRegister: boolean;
    enablePasswordRecovery: boolean;
    requireAccountConfirmation: boolean;
    corporateMode: boolean;

    emailDefaultAddress: string;
    emailDefaultName: string;

    logo: FilePostDto;
    background: FilePostDto;
}

export interface GetSetupUserDto extends SaveSetupUserDto {
    recordInfo: RecordInfo;
    counters: {
        blockCodeCounter: number;
        listLocalCounter: number;
    };
}

export interface SaveSetupUserDto {
    localName: string;
    editLocalInUserProfile: boolean;
    chooseLocalFromList: boolean;
    userWelcomeText: string;
}


export interface GetSetupPublicationDto {
    defaultApprover: UserShortView;
    approverOnlySuspectPublication: boolean;
    allowManagerEditPublications: boolean;

    specialistMinPubs: number;
    specialistMinLikes: number;
    specialistMinThanks: number;

    minScoreTopicPrediction: number;
    lastTopicClassificationTraining?: Date;
    resetTopicClassificationTraining?: boolean;

    institutionalName: string;
    thumbnail: FileGetDto;

    recordInfo: RecordInfo;
    tenantMediaModerationEnabled: boolean;
}

export interface SaveSetupPublicationDto {
    defaultApproverId: string;
    approverOnlySuspectPublication: boolean;
    allowManagerEditPublications: boolean;

    specialistMinPubs: number;
    specialistMinLikes: number;
    specialistMinThanks: number;

    minScoreTopicPrediction: number;
    resetTopicClassificationTraining: boolean;

    institutionalName: string;
    institutionalThumbnail: FilePostDto;
}

export interface SetupKnowledgeTestBaseFields {
    questionsBySession: number;
    retentionDaysInterval: number;
    showCorrectAnswer: boolean;
    sequential: boolean;
}

export interface GetSetupKnowledgeTestExceptionDto extends SetupKnowledgeTestBaseFields {
    topicId: string;
    topicName: string;
}

export interface GetSetupKnowledgeTestDto extends SetupKnowledgeTestBaseFields {
    tenantId: string;
    feedbackCorrect: string;
    feedbackIncorrect: string;
    consecutiveCorrectAnswers: number;
    useDefaultFeedbacks: boolean;
    recordInfo: RecordInfo;
    exceptions: GetSetupKnowledgeTestExceptionDto[];
}

export interface SaveSetupKnowledgeTestDto extends SetupKnowledgeTestBaseFields {
    feedbackCorrect: string;
    feedbackIncorrect: string;
    consecutiveCorrectAnswers: number;
    useDefaultFeedbacks: boolean;
    exceptions: GetSetupKnowledgeTestExceptionDto[];
}

export interface EventIntegrationInfoDto {
    eventId: IntegrationQueueEvent;
    postedOn: Date;
    lastStatus: string;
    lastStatusCode?: number;
    lastProcessingDate?: Date;
    queueCount: number;
}

export class GetSetupIntegrationDto {
    enableAzEnterpriseAppSso: boolean;
    azTenantId: string;
    azPrimaryDomain: string;
    applicationId: string;
    applicationObjectId: string;
    applicationSecret: string;
    applicationMsalUrl: string;

    enableSAML: boolean;
    samlLoginUrl: string;
    samlLogoutUrl: string;
    samlIdentifier: string;
    samlCertificateLoaded: boolean;
    objectIdClaimField: string;
    firstNameClaimField: string;
    lastNameClaimField: string;
    emailClaimField: string;
    locationClaimField: string;
    phoneClaimField: string;

    enableSiteSso: boolean;
    acceptSiteSsoFromUri: string;
    siteSingleSignOnKey: string;

    enableEventIntegration: boolean;
    eventIntegrationUrl: string;
    eventIntegrationKey: string;

    recordInfo: RecordInfo;
    eventIntegrationInfo: EventIntegrationInfoDto;
}

export interface SaveSetupIntegrationDto {
    enableAzEnterpriseAppSso: boolean;
    azTenantId: string;
    azPrimaryDomain: string;
    applicationId: string;
    applicationObjectId: string;
    applicationSecret: string;
    applicationMsalUrl: string;

    enableSAML: boolean;
    samlLoginUrl: string;
    samlLogoutUrl: string;
    samlIdentifier: string;
    samlCertificate: string;
    objectIdClaimField: string;
    firstNameClaimField: string;
    lastNameClaimField: string;
    emailClaimField: string;
    locationClaimField: string;
    phoneClaimField: string;

    enableSiteSso: boolean;
    acceptSiteSsoFromUri: string;
    siteSingleSignOnKey: string;

    enableEventIntegration: boolean;
    eventIntegrationUrl: string;
    eventIntegrationKey: string;
}

export interface BlockCodeDto {
    blockCodeId: string;
    description: string;
}

export interface LocationDto {
    locationId: string;
    description: string;
}

//#endregion

//#region FILTER: COURSE LIST

export enum CourseAdminListOrderBy {
    Title, Creation, MostEnrolls, MostLikes, MostComments
}

export interface CourseAdminListFilter {
    searchText: string;
    creationStartDate?: Date;
    creationEndDate?: Date;
    availableStartDate?: Date;
    availableEndDate?: Date;

    topicId?: string;
    showSubtopics: boolean;
    expiredOnly: boolean;
    authors?: string[];
    withAutoEnroll?: boolean;
    withoutAutoEnroll?: boolean;
    withAward?: boolean;
    withoutAward?: boolean;
}

export interface GetCourseAdminListParams {
    filter: CourseAdminListFilter;
    orderBy: CourseAdminListOrderBy;
    range: PageRange;
}

//#endregion

//#region FILTER: PUBLICATION LIST

export enum PublicationAdminListOrderBy {
    Title, MostRecent, MostViews, MostLikes, MostComments, MostThanks
}

export interface PublicationAdminListFilter extends FeedBaseFilter {
    topicId?: string;
    showBlocked: boolean;
    showSubtopics: boolean;

    publicationTypes?: PublicationType[];
    attachTypes?: FeedAttachType[];

    onlyInstitutional?: boolean;
    onlyNotInstitutional?: boolean;
    onlyBlocked?: boolean;
    deleted?: boolean;

    withLikes?: boolean;
    withThanks?: boolean;
    withComments?: boolean;
    withAnswers?: boolean;
    withDenouncement?: boolean;
    withNegativeLikes?: boolean;
    withOpenDenouncement?: boolean;

    authors?: string[];
    mentions?: string[];

    highlighted?: boolean;
    expired?: boolean;
    future?: boolean;
}

export interface GetPublicationAdminListParams {
    filter: PublicationAdminListFilter;
    orderBy: PublicationAdminListOrderBy;
    range: PageRange;
}


//#endregion

//#region FILTER: KNOWLEDGE LIST

export enum GetKnowledgeTestTopicsOrderBy {
    Name, NumberOfTests
}

export interface GetKnowledgeTestTopicsParamsFilter {
    topicId?: string;
    showSubtopics?: boolean;
    onlyWithTests?: boolean;
}

export interface GetKnowledgeTestTopicsParams {
    filter: GetKnowledgeTestTopicsParamsFilter;
    orderBy: GetKnowledgeTestTopicsOrderBy;
    range: PageRange;
}


export enum KnowledgeTestAdminListOrderBy {
    DifficultyLevel, CreateDate, MostAnswered, MostCorrect, MostWrong
}

export interface KnowledgeTestAdminListFilter {
    topicId: string;
    blockedOnly?: boolean;
    showBlocked?: boolean;

    searchText: string;
    creationStartDate?: Date;
    creationEndDate?: Date;
    availableStartDate?: Date;
    availableEndDate?: Date;

    levels?: KnowledgeTestDifficultyLevel[];
    importances?: KnowledgeTestImportance[];
    attachTypes?: FeedAttachType[];
    authors?: string[];
}

export interface GetKnowledgeTestAdminListParams {
    filter: KnowledgeTestAdminListFilter;
    orderBy: KnowledgeTestAdminListOrderBy;
    range: PageRange;
}


//#endregion

//#region FILTER: TOPIC LIST

export enum TopicListAdminOrderBy { Title, Followers, Publications }

export enum TopicListOrderBy { Title, Followers, Publications, Following, Unfollowing }

export enum TopicListFilterVisibility { Public, Private }

export interface TopicListFilter extends FeedBaseFilter {
    description: string;
    withApproval: boolean;
    withoutApproval: boolean;
    visibility: TopicListFilterVisibility;
}

export interface GetTopicListAdminParams {
    filter: TopicListFilter;
    orderBy: TopicListAdminOrderBy;
    range: PageRange;
}

export interface GetTopicListParams {
    searchText: string;
    orderBy: TopicListOrderBy;
    range: PageRange;
}

//#endregion

//#region FILTER: USER LIST

export enum UserListOrderBy { Name, LastUpdateDate }

export interface UserListFilter extends FeedBaseFilter {
    groups: string[];
    specialties: string[];
    knowsAbout: string[];
    blockCodes: string[];
    roles: string[];

    locations: string[];
    locationName: string;

    managerId: string;
    isManager: boolean;

    isBlocked: boolean;
    isNotBlocked: boolean;

    isLockedOut: boolean;
    isDeleted: boolean;
}

export interface GetUserListParams {
    filter: UserListFilter;
    orderBy: UserListOrderBy;
    range: PageRange;
}

//#endregion


/*****************************************************
 * FEED'S
 ****************************************************/

//#region Feed

export interface WorkFlowStepDto {
    approver: UserShortView;
    completedON: Date;
    status: ApprovalStatus;
    notes: string;
}

export interface CourseItemCompleteResult {
    courseItem: FeedItemInteractionCourseItemDto;
    courseEnroll: FeedItemInteractionCourseEnrollDto;
    award: AwardResult;
}

export interface FeedAttachCognitiveAnalysis {
    fileId: string;
    fileMimeType: string;
    cognitiveAnalysisReasons: PublicationCognitiveAnalysisReason;
}

export interface FeedTaskDto {
    requester: UserShortView;
    dueDate?: Date;
    description: string;
    status: string;
    commentsCounter: number;
}

export interface FeedApproverDto {
    approver: UserShortView;
    approvedOn?: Date;
    comment: string;
}

export interface FeedDenounceDto {
    denouncer: UserShortView;
    denouncedOn?: Date;
    message: string;
}

export interface FeedItemApprovalWorkFlow {
    comment: string;
    approver: UserShortView;
    requestedOn: Date;
    completedOn: Date;
}

export interface FeedItemBasePublicationDto {
    isAnonymous?: boolean;
    abuseReported: boolean;

    creationDate: Date;

    publishDate: Date;
    publisher: UserShortView;

    lastUpdateDate: Date;
    revisor: UserShortView;

    deleted: boolean;
    deleteDate?: Date;
    deleter?: UserShortView;

    blocked: boolean;
    blockedOn?: Date;
    blocker?: UserShortView;

    institutionalCreator?: UserShortView;

    parentId: string;
    parentPublicationType?: PublicationType;
}



export interface FeedItemPublicationDto extends FeedItemBasePublicationDto {
    title: string;

    topics: TopicSuggestionDto[];
    mainTopicId: string;

    attachMimeType: string;
    attachExtension: string;
    attachUrl: string;

    approvalStatus: ApprovalStatus;
    contentCognitiveAnalysisResult?: boolean;
    contentCognitiveAnalysisReasons?: PublicationCognitiveAnalysisReason;
    contentCognitiveAnalysisWords?: string;
    attachsCognitiveAnalysis: FeedAttachCognitiveAnalysis[];

    lastWorkflowStep: FeedItemApprovalWorkFlow;

    hasTopicRestriction: boolean;
    hasRelations: boolean;

    isInstitutional: boolean;
    isDraft?: boolean;
    isPublic?: boolean;
    inHighlightPeriod: boolean;
    inPriorityView: boolean;
    inExpiredPeriod: boolean;
    inFuturePeriod: boolean;
    isTheBestAnswer: boolean;
    bannedByAuthor: boolean;

    list?: FeedItemPublicationListDto,
    course?: FeedItemCourseDto;
    courseItem?: FeedItemCourseItemDto;
}

export interface FeedItemPublicationListDto {
    listType?: ListType;
}

export interface FeedItemCourseDto {
    gainPoints?: number;
    viewPeriodStart?: Date;
    viewPeriodEnd?: Date;
}

export interface FeedItemCourseItemDto {
    editStatus?: CourseItemStatus;
}

export interface FeedItemBestAnswerDto {
    publicationId: string;
    body: FeedItemBodyDto;
    publication: FeedItemBasePublicationDto;
    permissions: FeedItemPermissionsDto;
    interaction: FeedItemInteractionDto;
    statistics: FeedItemStatisticsDto;
}

export interface PublicationFeedPollItemResult {
    optionId: string;
    total: number;
}

export interface FeedItemBaseBodyDto {

    /**
     * Texto resumido
     **/
    summary: string;

    /**
     * Texto completo
     **/
    fullContent: string;

    /**
     * Define se o conteúdo completo foi carregado.
     * Se nulo, indica que o conteúdo completo é igual ao conteúdo resumido, portanto, não precisa ser recarregado.
     **/
    fullContentLoaded: boolean;

    /**
     * Primeira imagem do conteúdo
     **/
    imageDataOrUrl: string;

    /**
     * Marcado com Abuso de Conteúdo
     **/
    reportAbuseStamp: boolean;
}


export interface FeedItemBodyPollDto {
    options: PollItemModel[];
    results: PublicationFeedPollItemResult[];
    allowAnswersUntil: Date;
}

export interface FeedItemBodyDto extends FeedItemBaseBodyDto {
    metadata: ContentMetadataGetDto;
    attachs: FileThumbnaildGetDto[];
    poll: FeedItemBodyPollDto;
}

export interface FeedItemFooterDto {
    lastDenounce: FeedDenounceDto;
    task: FeedTaskDto;
    needApproval?: boolean;
}

export interface FeedItemExtraInfo {
    newAuthorPublicationName: string;
    newTopicPublicationName: string;
    newSurveyWithDueDate: Date;
    newPoolWithPoints: number;
    newSurvey: boolean;
    newCourse: boolean;
    newTask: boolean;

    topicWillLike: string;
    topicSuggested: string;
    mainTopicName: string;

    taskDueDate: Date;
    courseCompleteDate: Date;
}

export interface FeedItemPermissionsDto {
    hasAccess: boolean;
    canEdit: boolean;
    canEditRelationships: boolean;
    canBlock: boolean;
    canRequestUnblockPublication: boolean;
    canEditTopic: boolean;
    canAddHighlight: boolean;
    canCancelHighlight: boolean;
    canDelete: boolean;
    canAddItem?: boolean;
    canAddSuggestion?: boolean;
    canClose: boolean;
    canMarkComplete: boolean;
    canViewBody: boolean;
    canViewLater: boolean;
    canReportAbuse: boolean;
    canApprove: boolean;
}

export interface FeedItemInteractionCourseEnrollDto {
    enrolledOn?: Date;
    enrolledBy?: string;
    startedOn?: Date;
    completedOn?: Date;
    dueDate?: Date;
    expired: boolean;
    progress?: number;
    gainedPoints?: number;
}

export interface FeedItemInteractionCourseItemDto {
    createdOn: Date;
    completedOn: Date;
    containVideo: boolean;
    containEmbedVideo: boolean;
    initialPlaytime?: number;
    blockVideoSeek?: boolean;
}

export interface FeedItemInteractionPollDto {
    pollSelectedOptionId?: string;
}

export interface FeedItemInteractionDto {
    userId: string;

    canLike: boolean;
    canThanks: boolean;
    canReportAbuse: boolean;
    canAddToFavorites: boolean;
    canSharePublication: boolean;
    publicationTypeAllowInteraction: boolean;
    publicationTypeAllowThanks: boolean;

    visibility: PublicationVisility;

    likePosted?: PublicationLikeOption;
    thanksPostedMessage?: string;

    following: boolean;
    favorited?: boolean;
    thanked?: boolean;
    callout?: boolean;
    inViewLaterList: boolean;
    wasRecommended: boolean;
    hasRecommendations: boolean;

    lastAbuseReport?: Date;
    followingMainTopic: boolean;
    followingAuthor: boolean;

    poll: FeedItemInteractionPollDto;
    courseEnroll: FeedItemInteractionCourseEnrollDto;
    courseItem: FeedItemInteractionCourseItemDto;
}

export interface FeedItemCourseInteractionDto {
    userId: string;
}

export interface FeedItemActionRequesterDto {
    requester: UserShortView;
    requestedOn: Date;
    requesterComment?: string;
}

export interface FeedItemActionDto extends FeedItemActionRequesterDto {
    actionId: string;
    actionType: PublicationActionType;
    actionStatus: PublicationActionStatus;

    owner: UserShortView;
    ownerNotes: string,
    ownerResultStatus: ApprovalStatus;

    actionTitle: string;
    abuseReportCount: number;
    forYourAnswer: boolean;

    otherReports?: FeedItemActionRequesterDto[];
}

export class FeedItemDto {
    publicationId: string;
    publicationType: PublicationType;
    publication: FeedItemPublicationDto;
    body: FeedItemBodyDto;
    bestAnswer: FeedItemBestAnswerDto;
    footer: FeedItemFooterDto;
    interaction: FeedItemInteractionDto;
    statistics: FeedItemStatisticsDto;
    permissions: FeedItemPermissionsDto;
    action: FeedItemActionDto;
    extraInfo: FeedItemExtraInfo;

    // Local Properties
    isUpdating: boolean;
    isRemoved: boolean;
}

export interface FeedMultiHeaderDto {
    title: string;
    subtitle: string;
    thumbnailUrl: string;
}

export interface FeedContentDto {
    uid: string;
    multiHeader?: FeedMultiHeaderDto;
    items: FeedItemDto[];
}

export type FeedContentUpdate = {
    uid: string;
    multiHeader?: FeedMultiHeaderDto;
    items: DeepPartial<FeedItemDto>[];
};

export type FeedContentReplace = {
    uid: string;
    items: FeedItemDto[];
};

export interface FeedRequestDto {
    items: FeedContentDto[];
    total: number;
}

export interface FeedItemStatisticsDto {
    likes: number;
    dislikes: number;
    thanks: number;
    comments: number;

    views: number;
    answers: number;
    followers: number;
    enrollments: number;
    shared: number;

    suggestions?: number;
    approvals?: number;
    approvalPendings?: number;
}

export interface FeedTopicRecordDto {
    topicId: string;
    name: string;
    description: string;
    thumbnailUrl: string;
    lastPublication: Date;
    isPublic: boolean;
    containsTests: boolean;
}

export interface FeedTopicStatisticsDto {
    followers: number;
    publications: number;
}

export interface FeedTopicPermissionsDto {
    canEdit: boolean;
    canFollow: boolean;
    hasAccess: boolean;
}

export interface FeedTopicDto extends FeedTopicRecordDto {
    permissions: FeedTopicPermissionsDto;
    interaction: UserTopicInterationDto;
    relatedTopics: FeedTopicRecordDto[];
    statistics: FeedTopicStatisticsDto;
}

export interface PublicationUnblockDto {
    blocker: UserShortView;
    blockedOn?: Date;
    blockMessage: string;
    canUnblock: boolean;
}

export interface PublicationActionGetDto {
    actionId: string;
    message: string;
    actionType: PublicationActionType;
    actionStatus: PublicationActionStatus;
    requestedOn: Date;
    owner: UserShortView;
    ownerNotes: string;
    ownerResultStatus?: ApprovalStatus;
    completedOn?: Date;
}

export interface PublicationAdminDto {
    answers: number;
    creationDate: Date;
    publisher: UserShortView;
    title: string;
    topics: TopicSuggestionDto[];

    abuseReported: boolean;
    blocked: boolean;
    inHighlightPeriod: boolean;
    inExpiredPeriod: boolean;
    inFuturePeriod: boolean;
    deleted: boolean;
    isInstitutional: boolean;

    videoFileId: string;
    videoFilename: string;
    videoContainsSubtitles: boolean;
    containsVttMetadata: boolean;
}

export interface PublicationAdminPermissionsDto {
    canDelete: boolean;
    canEdit: boolean;
    canAddHighlight: boolean;
    canCancelHighlight: boolean;
    canEditRelationships: boolean;
}

export interface PublicationAdminItemDto {
    publicationId: string;
    publicationType: PublicationType;
    publication: PublicationAdminDto;
    statistics: FeedItemStatisticsDto;
    permissions: PublicationAdminPermissionsDto;

    // Client only properties
    body: FeedItemBodyDto;
    bodyLoading: boolean;
}


export interface PublicationAdminCourseStatisticsDto {
    enrollments: number;
    comments: number;
    likes: number;
}

export interface PublicationAdminCoursePermissionsDto {
    canDelete: boolean;
    canEdit: boolean;
}

export interface PublicationAdminCourseDto {
    publicationId: string;
    title: string;
    body: FeedItemBodyDto;
    topics: TopicSuggestionDto[];
    recordInfo: RecordInfo;
    statistics: PublicationAdminCourseStatisticsDto;
    permissions: PublicationAdminCoursePermissionsDto;

    viewPeriodStart: Date;
    viewPeriodEnd: Date;
    publishDate: Date;
    expired: boolean;

    autoEnroll: boolean;
    gainPoints: number;
    numberOfItems: number;

    mustBePublished: boolean;
}


export interface CourseEnrollReviewDto {
    user: UserShortView;
    progress: number;
    enrolledOn: Date;
    startedOn: Date;
    completedOn: Date;
}


export interface PublicationLikeReviewItemDto {
    user: UserShortView;
    likeOption: PublicationLikeOption;
    date: Date;
}

export interface PublicationLikeReviewDto {
    items: PublicationLikeReviewItemDto[];
    total: number;
}

export interface PublicationThanksReviewItemDto {
    user: UserShortView;
    message: string;
    date: Date;
}

export interface PublicationThanksReviewDto {
    userThanked: PublicationThanksReviewItemDto;
    items: PublicationThanksReviewItemDto[];
    total: number;
}

export interface PublicationViewersItemDto {
    user: UserShortView;
    date: Date;
    views: number;
}

export interface PublicationViewersDto {
    items: PublicationViewersItemDto[];
    total: number;
}


export interface PublicationApprovalResultDto {
    saved: boolean;
    callouts: NotificationCalloutDto[];
}

export interface PublicationRelationDto {
    publicationId: string;
    publicationType: PublicationType;
    title: string;
    ownerName: string;
    creationDate: Date;
}

export interface PublicationRelationshipDto {
    publicationId: string;
    publicationType: PublicationType;
    title: string;
    creationMode: RelationshipCreationMode;
}

//#endregion

//#region FILTER: BASE

export interface PageRange {
    offset: number;
    take: number;
}

export interface FeedBaseFilter {
    searchText: string;
    startDate?: Date;
    endDate?: Date;
}

export enum FeedBaseOrderBy { Default, Best, MostViews, MostComments, MostThanks }

export enum FeedAttachType {
    Video,
    Image,
    File,
}

//#endregion

//#region FILTER: PUBLICATION FEED

export interface FeedPublicationFilter extends FeedBaseFilter {
    category: FeedType;
    publicationTypes?: PublicationType[];
    attachTypes?: FeedAttachType[];
    withLikes?: boolean;
    withThanks?: boolean;
    withComments?: boolean;
    withNewLikes?: boolean;
    withNewThanks?: boolean;
    withNewComments?: boolean;
    approvedOnly?: boolean;
    rejectedOnly?: boolean;
    pendingOnly?: boolean;
    highlighted?: boolean;
    expired?: boolean;
    future?: boolean;
}

export interface FeedSessionParams {
    sessionId: string;
}

export interface GetFeedMainParams extends FeedSessionParams {
    range: PageRange;
}

export interface GetFeedPublicationParams {
    filter: FeedPublicationFilter;
    orderBy: FeedBaseOrderBy;
    range: PageRange;
}

//#endregion

//#region FILTER: ACTION FEED

export interface FeedActionsFilter {
    category?: FeedType;
    publicationId?: string;
}

export interface GetFeedActionsParams extends FeedSessionParams {
    filter: FeedActionsFilter;
    range: PageRange;
}

//#endregion

//#region FILTER: HISTORY FEED

export enum FeedHistoryCategory { Recent, Liked, Commented, Thanks, Shared }

export interface FeedHistoryFilter extends FeedBaseFilter {
    publicationTypes?: PublicationType[];
    category: FeedType;
    hasVideo?: boolean;
    hasImage?: boolean;
    hasFile?: boolean;
}

export interface GetFeedHistoryParams {
    filter: FeedHistoryFilter;
    range: PageRange;
}

//#endregion

//#region FILTER: HIGHLIGTHS FEED

export interface FeedHighlightsFilter extends FeedBaseFilter {
    category: FeedType;
    period?: FeedFollowingPeriod;
    publicationType?: PublicationType;
    attachType?: FeedAttachType;
}

export interface GetFeedHighlightsParams extends FeedSessionParams {
    filter: FeedHighlightsFilter;
    range: PageRange;
}

//#endregion

//#region FILTER: TOPIC FEED

export enum FeedTopicOrderBy { Recent, Best, MostComments, Priority }

export enum FeedTopicCategory { Read, Answer, BestPeople }

export interface FeedTopicFilter extends FeedBaseFilter {
    topicId: string;
    secondTopicId: string;
    category: FeedTopicCategory;
    publicationTypes: PublicationType[];
    attachTypes: FeedAttachType[];
    authors: string[];
    myPublication: boolean;
}

export interface GetFeedTopicParams {
    filter: FeedTopicFilter;
    orderBy: FeedTopicOrderBy;
    range: PageRange;
}

//#endregion

//#region FILTER: QUESTIONS FEED


export interface FeedQuestionsFilter {
    category: FeedType;
}

export interface GetFeedQuestionsParams extends FeedSessionParams {
    filter: FeedQuestionsFilter;
    range: PageRange;
}

//#endregion

//#region FILTER: FOLLOWING FEED

export enum FeedFollowingPeriod { Today, LastWeek, LastMonth, LastQuarter, LastYear }

export interface FeedFollowingFilter {
    period: FeedFollowingPeriod;
    publicationType?: PublicationType;
    attachType?: FeedAttachType;
}

export interface GetFeedFollowingParams extends FeedSessionParams {
    filter: FeedFollowingFilter;
    orderBy: FeedBaseOrderBy;
    range: PageRange;
}

//#endregion

//#region FILTER: LIST FEED

export enum FeedListOrderBy { RecentlyAdded, MostRecent, MostLiked, MostViewed, MostComments }

export interface FeedListFilter extends FeedBaseFilter {
    listId?: string;

    publicationTypes?: PublicationType[];
    attachTypes?: FeedAttachType[];
    withLikes?: boolean;
    withThanks?: boolean;
    withComments?: boolean;
    authors?: string[];
}

export interface GetFeedListParams extends FeedSessionParams {
    filter: FeedListFilter;
    orderBy: FeedListOrderBy;
    range: PageRange;
}

//#endregion

//#region FILTER: COURSE FEED

export enum FeedCourseOrderBy {
    Title,
    MostRecent,
    MostEnrolled,
    MostLiked,
    MostCommented,
    LessProgress,
    LessDeadline
}

export interface FeedCourseFilter extends FeedBaseFilter {
    category: FeedType;
    topicId?: string;
    courseId?: string;
    withAward?: boolean;
    notStarted?: boolean;
    expiring?: boolean;
}

export interface GetFeedCourseParams {
    filter: FeedCourseFilter;
    orderBy: FeedCourseOrderBy;
    range: PageRange;
}


export interface FeedCourseEnrollFilter {
    courseId: string;
    searchText?: string;
    attachTypes?: FeedAttachType[];
}

export interface GetFeedCourseEnrollParams {
    filter: FeedCourseEnrollFilter;
    range: PageRange;
}


export interface FeedCourseListFilter {
    courseId: string;
    searchText?: string;
    attachTypes?: FeedAttachType[];
    showDeleted?: boolean;
}

export interface GetFeedCourseListParams {
    filter: FeedCourseListFilter;
    range: PageRange;
}

//#endregion


//#region FILTER: USER PROFILE FEED

export interface TimelineUserProfileFilter extends FeedBaseFilter {
    userId?: string;
    publicationTypes?: PublicationType[];
    attachTypes?: FeedAttachType[];
    category: FeedType;
}

export interface GetFeedUserProfileParams extends FeedSessionParams {
    filter: TimelineUserProfileFilter;
    orderBy: FeedBaseOrderBy;
    range: PageRange;
}

//#endregion

//#region FILTER: ANSWERS FEED

export enum FeedAnswersOrderBy { Recent, Best, MostViews, MostCommented }

export interface FeedAnswersFilter extends FeedBaseFilter {
    questionId: string;
    answerId: string;
    answersFromUser?: string;
    authorId: string;
    videoOnly: boolean;
}

export interface GetFeedAnswersParams {
    filter: FeedAnswersFilter;
    orderBy: FeedAnswersOrderBy;
    range: PageRange;
}
//#endregion


/*****************************************************
 * TIPOS DE PUBLICAÇÃO
 ****************************************************/

//#region PUBLICATION COMMON

export interface PublicationShortView {
    publicationId?: string;
    publicationType: PublicationType;
    title: string;
    owner: UserShortView;
    viewUrl: string;
    thumbnailUrl: string;
}

interface BasePublicationPostDto {
    publicationId?: string;
    title: string;
    contentBody?: string;
    topics: string[];
    relativesTopics: string[];
}

interface BasePublicationGetDto {
    publicationId?: string;
    title: string;
    contentBody?: string;
    contentText?: string;
    topics: TopicSuggestionDto[];
    relativesTopics: TopicSuggestionDto[];
    deletedRelativesTopics: string[];

    owner?: UserShortView;
    lastUpdater?: UserShortView;
    creationDate?: Date;
    lasUpdateDate?: Date;

    parentId?: string;

    editPermissions: PublicationEditPermissions;
}

export interface PublicationAdvancedOptionsDto {
    enabled: boolean;
    isInstitutional: boolean;
    viewPeriodStart?: Date;
    viewPeriodEnd?: Date;
    viewHighlightUntil?: Date;
    viewPriority?: PublicationViewPriority;
}

export interface SearchPublicationDto {
    publicationId: string;
    publicationType: PublicationType;
    title: string;
    firstImageDataOrUrl: string;
    creatorId: string;
    creatorName: string;
    creatorThumnailUrl: string;
    creationDate: Date;
}

export interface SavePublicationResult {
    publicationId: string;
    publicationType: PublicationType;
    isDraft: boolean;
    approved: boolean;
    topics: string[];
    award: AwardResult;
    isNewRecord: boolean;
    viewPeriodStart?: Date;
}

export interface PublicationSuggestionDto extends AutoCompleteDataModel {
    publicationType?: PublicationType;
}


//#endregion

//#region POST PUBLICATION

export interface GetPostDto extends BasePublicationGetDto {
    attachs?: FileThumbnaildGetDto[];
    metadata?: ContentMetadata;
    advOptions: PublicationAdvancedOptionsDto;
    isDraft: boolean;
}

export interface SavePostDto extends BasePublicationPostDto {
    attachs?: FileThumbnailPostDto[];
    metadata?: ContentMetadata;
    advOptions: PublicationAdvancedOptionsDto;
    isDraft: boolean;
    parentId?: string;
}


//#endregion

//#region ARTICLE PUBLICATION

export interface GetArticleDto extends BasePublicationGetDto {
    institutional: PublicationAdvancedOptionsDto;
    isDraft: boolean;
}

export interface SaveArticleDto extends BasePublicationPostDto {
    institutional: PublicationAdvancedOptionsDto;
    isDraft: boolean;
    parentId?: string;
}

//#endregion

//#region LIST PUBLICATION

export interface GetListDto extends BasePublicationGetDto {
    attachs?: FileThumbnaildGetDto[];
    listType: ListType;
    advOptions: PublicationAdvancedOptionsDto;
    isPublic: boolean;
    allowSuggestions: boolean;
}

export interface SaveListDto extends BasePublicationPostDto {
    attachs?: FileThumbnailPostDto[];
    advOptions: PublicationAdvancedOptionsDto;
    isPublic: boolean;
    allowSuggestions: boolean;
}

export interface ListContentDto {
    listId: string;
    isPublic: boolean;
    publicationId: string;
    publicationTitle: string;
}

//#endregion


//#region COURSE PUBLICATION

export interface CourseRecordFormConfigDto {
    courseCompletePoints: number;
}

export interface GetCourseDto extends BasePublicationGetDto {
    attachs?: FileThumbnaildGetDto[];
    viewPeriodStart?: Date;
    viewPeriodEnd?: Date;
    gainPoints: number;
    autoEnroll: boolean;
    viewAllVideo: boolean;
    reviewAfterChanged: boolean;
}

export interface SaveCourseDto extends BasePublicationPostDto {
    attachs?: FileThumbnailPostDto[];
    viewPeriodStart?: Date;
    viewPeriodEnd?: Date;
    gainPoints: number;
    autoEnroll: boolean;
    viewAllVideo: boolean;
    reviewAfterChanged: boolean;
    updatePoints: boolean;
}

export interface CourseContentDto {
    courseId: string;
    publicationId: string;
    publicationTitle: string;
}

export interface CourseItemOperationResult {
    saved: boolean;
    mustBePublished: boolean;
    changed: boolean;
    hardDelete: boolean;
}

//#endregion


//#region POOL PUBLICATION

export interface PollItemModel {
    id: string;
    text: string;
}

export interface GetPollDto extends BasePublicationGetDto {
    answers: PollItemModel[];
    allowAnswersUntil?: Date;
    hasAnswers: boolean;
}

export interface SavePollDto extends BasePublicationPostDto {
    answers: PollItemModel[];
    allowAnswersUntil?: Date;
}

export class PollManager {
    constructor(data: PollItemModel[]) {
        if (data) this.answers = data;
        else {
            this.answers = [];
            this.addAnswer("");
            this.addAnswer("");
        }
    }

    answers: PollItemModel[];

    addAnswer = (text: string) => {
        this.answers.push({ id: LibUtils.newGuid(), text });
    };

    get isValid() {
        return this.answers.every(v => v.text.length > 0);
    }
}

//#endregion

//#region QUESTION PUBLICATION

export interface GetQuestionDto extends BasePublicationGetDto {
    attachs?: FileThumbnaildGetDto[];
    metadata?: ContentMetadata;
    isAnonymous: boolean;
}

export interface SaveQuestionDto extends BasePublicationPostDto {
    attachs?: FileThumbnailPostDto[];
    metadata?: ContentMetadata;
    isAnonymous: boolean;
    requestAnswerFromUser: string;
}

//#endregion

//#region QUESTION ANSWER PUBLICATION

export interface GetAnswerDto extends BasePublicationGetDto {
    questionId: string;
    questionTitle: string;
    isAnonymous: boolean;
    isDraft: boolean;
    isBestWanswer: boolean;
    attachs?: FileThumbnaildGetDto[];
    metadata?: ContentMetadata;
}

export interface SaveAnswerDto extends BasePublicationPostDto {
    questionId: string;
    isAnonymous: boolean;
    isDraft: boolean;
    attachs?: FileThumbnailPostDto[];
    metadata?: ContentMetadata;
}


//#endregion

//#region PUBLICATION COMMENTS

export enum CommentListOrderBy { PublishDate, Best }

export interface PostCommentDto {
    publicationId: string;
    parentId?: string;
    replyCommentId?: string;
    message: string;
    publisherId: string;
}

export interface DeleteCommentResult {
    deletedComments: CommentListItemDto[];
    changedComments: CommentListItemDto[];
    totalComments: number;
}

export interface NewCommentResult {
    comment: CommentListItemDto;
    totalComments: number;
}

export interface CommentItemActionsDto {
    canLike: boolean;
    canReportAbuse: boolean;
    canEdit: boolean;
    canDelete: boolean;
}

export interface CommentItemInteractionDto {
    likes: number;
    liked?: PublicationLikeOption;
    likedOn?: Date;
    lastAbuseReport?: Date;
    actions: CommentItemActionsDto;
}


export interface CommentListItemDto {
    commentId: string;
    parentId?: string;
    replyCommentId?: string;
    message: string;
    publisher: UserShortView;
    replyPublisher: UserShortView;
    deleter: UserShortView;
    publishDate: Date;
    lastUpdateDate: Date;
    deleted: boolean;
    answers: number;
    abuseReported: boolean;
    interaction: CommentItemInteractionDto;

    loading?: boolean;
    responding?: boolean;
    editing?: boolean;
}

export interface CommentListDto {
    comments: CommentListItemDto[];
    totalRecords: number;
    totalThreads: number;
    readOnly: boolean;
}


export interface GetCommentListParams {
    publicationId: string;
    commentId?: string;
    lastIndex: number;
    take: number;
    orderBy: CommentListOrderBy;
    referenceDate: Date;

    searchText: string;
    startDate?: Date;
    endDate?: Date;
    authors?: string[];
    findCommentId?: string;
}

//#endregion

//#region SOCIAL

export interface PublicationLookupFavoriteDto {
    isPublic: boolean;
    listId: string;
    listType: ListType;
    title: string;
    check: boolean;
}

export interface PublicationLookupTopicDto {
    topicId: string;
    title: string;
    thumbnailUrl: string;
}


export interface PostFavoriteItemDto {
    listId: string;
    itemId: string;
    check: boolean;
}

export interface PostNewFavoriteListDto {
    itemId: string;
    title: string;
    isPublic: boolean;
}

export interface PostSeeLaterDto {
    publicationId: string;
    check: boolean;
}


export interface PostLikeDto {
    objectId: string;
    likeOption: PublicationLikeOption;
}


export interface PublicationShareDto {
    publicationId: string;
    members: MemberDto[];
    message: string;
    shareType: PublicationShareType;
}


export interface PublicationSocialActionResult {
    interaction: {
        likePosted?: PublicationLikeOption;
        thanked?: boolean;
        thanksPostedMessage?: string;
    };
    statistics: {
        likes: number;
        dislikes: number;
        thanks: number;
    };
}

//#endregion


//#region Storage

export interface MediaServiceFileDto {
    fileId: string;
    localFilename: string;
    originalFilename: string;
    fileSize?: number;
    creationDate: Date;

    processingStatus?: MediaServicesProcessingStatus;
    jobResult: string;

    publicationId?: string;
    publicationType?: PublicationType;
    publicationParentId?: string;
    publicationTitle: string;

    testeId?: string;
    testTitle: string;
}

export interface GetMediaFileListFilter {
    searchText: string;
    processingStatus: MediaServicesProcessingStatus[];
}

export interface GetMediaFileListParams {
    filter: GetMediaFileListFilter;
    range: PageRange;
}

//#endregion

/*****************************************************
 * DASHBOARDS
 ****************************************************/

//#region DASHBOARD COMMOM

export enum DashboardPeriod { All, Last30, Day }

export enum DashboardInstitutionalFilterOption { Both, OnlyInstitutional, OnlyNonInstitutional }

export interface DashboardPublicationModel {
    publicationId: string;
    publicationType: PublicationType;
    title: string;
    thumbnailUrl: string;
    firstTopicName?: string;
    isInstitutional?: boolean;

    creationDate: Date;
    creatorId?: string;
    creatorName?: string;
    publishDate?: Date;

    detailCount?: number;
    total: number;

    totalRows: number;
}

export interface DashboardUserEnrollModel {
    userId: string;
    name: string;
    thumbnailUrl: string;
    enrolledOn: Date;
    completedOn: Date;
}


export interface DashboardUserModel {
    userId: string;
    name: string;
    thumbnailUrl: string;

    detailCount: number;
    total: number;
    totalRows: number;
}

export interface DashboardUserKnowledgeTestModel {
    userId: string;
    name: string;
    thumbnailUrl: string;

    detailCount: number;
    total: number;
    correct: number;
    wrong: number;

    totalRows: number;
}


export interface DashboardUserProgressModel {
    userId: string;
    name: string;
    thumbnailUrl: string;

    startedOn?: Date;
    progress: number;
    totalRows: number;
}

export interface DashboardKnowledgeTestModel {
    testId: string;
    title: string;
    total?: number;
    percent?: number;
    totalRows: number;
}


export interface DashboardTopicModel {
    topicId: string;
    name: string;
    thumbnailUrl: string;

    detailCount?: number;
    total?: number;
    percent?: number;

    totalRows: number;
}

export interface DashboardTopicKnowledgeTestModel {
    topicId: string;
    name: string;
    thumbnailUrl: string;

    detailCount?: number;
    total?: number;
    correct?: number;
    wrong?: number;
    percent?: number;

    totalRows: number;
}


export interface DashboardGraphModel {
    day: Date;
    total: number;
}

//#endregion

//#region DASHBOARD KNOWLEDGE COMMON

export interface DashboardGraphByGroupModel {
    f0: number;
    f1: number;
    f2: number;
    f3: number;
    f4: number;
    f5: number;
    f6: number;
}

//#endregion

//#region DASHBOARD: KNOWLEDGE TEST INDICATORS

export enum DashboardKnowledgeIndicatorsGraphGroup { User, Topic }
export enum DashboardKnowledgeIndicatorsSummaryColumn { Resume, Knowledge, Result, Completed, Revision, Trust, Engagement }
export enum DashboardKnowledgeIndicatorsDetailColumn { Graph, UserBetter, UserWorst, UserLevel, UserQt, TopicBetter, TopicWorst, TopicLevel, TopicQt, UserTopicBetter, UserTopicWorst, UserTopicLevel, UserParticipate, UserParticipateLevel }

export interface DashboardKnowledgeIndicatorsResumeModel {
    knowledge: number;
    completed: number;
    result?: number;
    revision?: number;
    trust?: number;
    engagement?: number;
}

export interface DashboardKnowledgeIndicatorsDataModel {
    topicId: string;
    thumbnailUrl: string;
    name: string;
    currentLevel: KnowledgeTestDifficultyLevel;
    resultPercent: number;
    revisionPercent: number;
    completedPercent: number;
    knowledgePercent: number;
    trustPercent: number;
    revisionAnsweredTests: number;
    revisionCorrectTests: number;
    answeredTests: number;
    correctTests: number;
    totalTopicTests: number;
    totalUnanswered: number;
    totalWrong: number;
    totalAvailableTests: number;
    activeAnsweredTests: number;
    totalRows: number;
}

export interface DashboardKnowledgeIndicatorsDrillDownModel {
    id: string;
    thumbnailUrl: string;
    name: string;
    currentLevel: KnowledgeTestDifficultyLevel;
    resultPercent: number;
    revisionPercent: number;
    completedPercent: number;
    knowledgePercent: number;
    trustPercent: number;
}

export interface DashboardKnowledgeIndicatorsDetailModel {
    id: string;
    thumbnailUrl: string;
    name: string;
    avgResultPercent?: number;
    avgRevisionPercent?: number;
    avgCompletedPercent?: number;
    avgKnowledgePercent?: number;
    avgTrustPercent?: number;
    total: number;
}


export interface DashboardKnowledgeTestIndicatorsParams extends DashboardUserAndGroupsAndManagersBaseFilter {
    defaultGraphGroup: DashboardKnowledgeIndicatorsGraphGroup;
    columnToSummary: DashboardKnowledgeIndicatorsSummaryColumn;
    detailColumn?: DashboardKnowledgeIndicatorsDetailColumn;
    selectedValue?: number;
    drillDownId?: string;
    drillDownSearchText?: string;
    range?: PageRange;
}

//#endregion

//#region DASHBOARD: KNOWLEDGE TEST APPLIED 

export enum DashboardKnowledgeTestAppliedSummaryColumn { Resume, Answered, Result, Trust }

export enum DashboardKnowledgeTestAppliedDetailColumn {
    Graph,
    WhoMostAnswered, TopicMostAnswered,
    TestMostWrong, TestMostRight, TestRightLowTrust, TestWrongHighTrust, TestResultRange,
    TestLessTrust, TestMostTrust, TestTrustRange
}

export interface DashboardKnowledgeTestAppliedResumeModel {
    totalAnswered: number;
    answered30: number;
    frequentlyWrong: number;
    frequentlyTrustLow: number;
}

export interface DashboardKnowledgeTestAppliedDetailModel {
    id: string;
    name: string;
    thumbnailUrl: string;
    range: number;
    total: number;
    totalRows: number;
}

export interface DashboardKnowledgeTestAppliedAnsweredGraphModel {
    day: Date;
    total: number;
    totalRight: number;
    avgTrustLevel: number;
}

export interface DashboardKnowledgeTestAppliedParams extends DashboardUserAndGroupsAndManagersBaseFilter {
    columnToSummary: DashboardKnowledgeTestAppliedSummaryColumn;
    detailColumn?: DashboardKnowledgeTestAppliedDetailColumn;
    startDate?: Date;
    endDate?: Date;
    selectedValue?: number;
    range?: PageRange;
}

//#endregion

//#region DASHBOARD: KNOWLEDGE TEST ANALYSIS

export enum DashboardKnowledgeTestAnalysisSummaryColumn { Resume, Published, Answered, MissingTests }

export enum DashboardKnowledgeTestAnalysisDetailColumn {
    Graph, GraphByDay, GraphByMonth,
    WhoPublished, TopicWithMoreTests,
    TopicExausted, TopicExaustedLevel,
    TopicMissingTests
}

export interface DashboardKnowledgeTestAnalysisResumeModel {
    totalAnswered: number;
    totalPublished: number;
    totalPublished30: number;
    topicsMissingTests: number;
}

export interface DashboardKnowledgeTestAnalysisParams {
    columnToSummary: DashboardKnowledgeTestAnalysisSummaryColumn;
    detailColumn?: DashboardKnowledgeTestAnalysisDetailColumn;
    startDate?: Date;
    endDate?: Date;
    selectedValue?: number;
    topicId: string;
    range?: PageRange;
}

export interface DashboardKnowledgeAnalysisMissingTestsModel {
    topicId: string;
    name: string;
    thumbnailUrl: string;
    beginner: number;
    intermediate: number;
    advanced: number;
}

export interface DashboardKnowledgeTestAnalysisMissinGraphModel {
    difficultyLevel: KnowledgeTestDifficultyLevel;
    total: number;
}

//#endregion

//#region DASHBOARD: USER EVOLUTION

export enum DashboardUserEvolutionPublicationListColumn { Views, Comments, Likes, Thanks }

export interface DashboardUserEvolutionPublicationParams {
    userId: string;
    period: DashboardPeriod;
    column: DashboardUserEvolutionPublicationListColumn;
    day?: Date;
    range: PageRange;
}

export interface DashboardUserEvolutionPublicationResumeModel {
    days: {
        day: Date;
        views: number;
        thanks: number;
        likes: number;
        comments: number;
    }[];
    views: number;
    thanks: number;
    likes: number;
    comments: number;
}

export enum DashboardUserEvolutionParticipationColumn { Publications, Answers }

export interface DashboardUserEvolutionParticipationParams {
    userId: string;
    column: DashboardUserEvolutionParticipationColumn;
    day?: Date;
    range: PageRange;
}

export interface DashboardUserEvolutionPublicationListModel {
    publicationId: string;
    publicationType: PublicationType;
    title: string;
    thumbnailUrl: string;
    creationDate: Date;
    parentId: string;
    columnTotal: number;
    publicationTotal: number;
}


export interface DashboardUserEvolutionParticipationResumeModel {
    days: {
        day: Date;
        publications: number;
        answers: number;
    }[];
    publications: number;
    answers: number;
}

export interface DashboardUserEvolutionParticiationListModel {
    publicationId: string;
    publicationType: PublicationType;
    title: string;
    thumbnailUrl: string;
    creationDate: Date;
    topicName: string;
}


export enum DashboardUserEvolutionKnowledgeDetailColumn { Graph = 1, Better = 2, Worst = 3, Selected = 4 }

export interface DashboardUserEvolutionKnowdledgeParams {
    userId: string;
    summaryColumn: DashboardKnowledgeIndicatorsSummaryColumn;
    detailColumn: DashboardUserEvolutionKnowledgeDetailColumn;
    selectedValue?: number;
    range?: PageRange;
}

//#endregion

//#region DASHBOARD: User and Groups

export interface DashboardUserAndGroupsBaseFilter {
    memberId: string;
    memberType: MemberType;
    topicId: string;
    includeSubtopics: boolean;

    topicName?: string;
    topicThumbnailUrl?: string;
    memberName?: string;
    memberThumbnailUrl?: string;
}

export interface DashboardUserAndGroupsAndManagersBaseFilter extends DashboardUserAndGroupsBaseFilter {
    managerId: string;
    managerDeepTeam: boolean;
}

export enum DashboardUserAndGroupsParticipationSummaryColumn { Resume, Accesses, Publications, Answers, KnowledgeTests }
export enum DashboardUserAndGroupsParticipationPublicationDetailColumn { Publications, Author, AuthorDrillDown, Topic, TopicDrillDown, GraphByDay, GraphByMonth }
export enum DashboardUserAndGroupsParticipationAccessDetailColumn { Access, NotAccess, Graph }
export enum DashboardUserAndGroupsParticipationKnowledgeDetailColumn { UserMoreAnswers, TopicMoreAnswers, TopicMoreTrust, Graph }

export interface DashboardUserAndGroupsParticipationResumeModel {
    activeUsers: number;
    accesses: number;
    accesses7: number;
    publications: number;
    publications30: number;
    answers: number;
    answers30: number;
    knowledgeTests: number;
    knowledgeTests30: number;
}

export interface DashboardUserAndGroupsParticipationKnowledgeGraphModel {
    day: Date;
    answered: number;
    correct: number;
    trustLevel: number;
}

export interface DashboardUserAndGroupsParticipationBaseFilter extends DashboardUserAndGroupsAndManagersBaseFilter {
    startDate?: Date;
    endDate?: Date;
}

export interface DashboardUserAndGroupsFilter extends DashboardUserAndGroupsParticipationBaseFilter {
    publicationTypes: PublicationType[];
    institutionalOption: DashboardInstitutionalFilterOption;
    withVideo?: boolean;
    activeOnly?: boolean;
}

export interface DashboardUserAndGroupsParticipationPublicationParams extends DashboardUserAndGroupsFilter {
    columnToSummary: DashboardUserAndGroupsParticipationSummaryColumn;
    detailColumn?: DashboardUserAndGroupsParticipationPublicationDetailColumn;
    range?: PageRange;
    drillDownFilter?: {
        id: string;
        title?: string;
    };
}

export interface DashboardUserAndGroupsParticipationAccessParams extends DashboardUserAndGroupsParticipationBaseFilter {
    detailColumn: DashboardUserAndGroupsParticipationAccessDetailColumn;
    range?: PageRange;
}

export interface DashboardUserAndGroupsParticipationKnowledgeTestParams extends DashboardUserAndGroupsParticipationBaseFilter {
    detailColumn: DashboardUserAndGroupsParticipationKnowledgeDetailColumn;
    range?: PageRange;
}

//#endregion

//#region DASHBOARD: PUBLICATION ANALYSIS

export enum DashboardPublicationAnalysisSummaryColumn { Resume, Publications, Views, Comments, Likes, Thanks }
export enum DashboardPublicationAnalysisDetailColumn {
    Publications, PublicationsDrillDown,
    Author, AuthorDrillDown, AuthorPub,
    Responder, ResponderDrillDown,
    Topic, TopicDrillDown,
    TopicQuestion, TopicQuestionDrillDown,
    TopicQuestionNoAnswer, TopicQuestionNoAnswerDrillDown,
    GraphByDay, GraphByMonth
}

export interface DashboardPublicationAnalysisParams extends DashboardPublicationAnalysisFilter {
    columnToSummary: DashboardPublicationAnalysisSummaryColumn;
    detailColumn?: DashboardPublicationAnalysisDetailColumn;
    startDate?: Date;
    endDate?: Date;
    range?: PageRange;
    drillDownFilter?: {
        id: string;
        title?: string;
    };
}

export interface DashboardPublicationAnalysisFilter {
    topicId: string;
    includeSubtopics: boolean;

    authorId: string;
    viewerId: string;

    publicationTypes: PublicationType[];
    institutionalOption: DashboardInstitutionalFilterOption;
    withVideo?: boolean;
    withTitle?: string;
    activeOnly?: boolean;
}

export interface DashboardPublicationAnalysisResumeModel {
    publications: number;
    publications30: number;
    views: number;
    views30: number;
    comments: number;
    comments30: number;
    thanks: number;
    thanks30: number;
    likes: number;
    likes30: number;
}


//#endregion

//#region DASHBOARD: AWARD ANALYSIS

export enum DashboardAwardAnalysisDetailColumn { Resume, GraphAwardMonthly, AwardMonthly, AwardType, AwardTypeDrillDown, Badges, BadgesDrillDown }
export enum DashboardAwardAnalysisPeriod { Total, Last30 }

export interface DashboardAwardAnalysisFilter {
    memberId?: string;
}

export interface DashboardAwardAnalysisParams extends DashboardAwardAnalysisFilter {
    detailColumn: DashboardAwardAnalysisDetailColumn;
    startDate: Date;
    endDate: Date;
    range?: PageRange;
    drillDownFilter?: {
        id: any;
        title?: string;
    };
}

export interface DashboardAwardAnalysisResumeModel {
    totalPoints: number;
    totalPointsByUser: number;
    totalBadges: number;
    totalBadgesByUser: number;
}

export interface DashboardAwardTypeModel {
    awardType: AwardType;
    total: number;
}

export interface DashboardBadgeModel {
    badgeId: string;
    name: string;
    thumbnailUrl: string;
    total: number;
}

//#endregion

//#region DASHBOARD: COURSE ADMIN ANALYSIS

export interface DashboardCourseAdminAnalysisModel {
    enrollments: number;
    notInitiate: number;
    inProgress: number;
    completed: number;
    noProgress30: number;
    completeInOneWeek: number;
}

//#endregion


//#region DASHBOARD: COURSE ANALYSIS

export enum DashboardCourseAnalysisSummaryColumn { Resume, Enrollments, Progress, Completed, Deadline, CommonProgress, Comments, Likes, Published }

export enum DashboardCourseAnalysisDetailColumn {
    GraphByDay, GraphByMonth, GraphByGroup,

    Courses, CoursesDrillDown,
    CoursesMostCompleted, CoursesMostCompletedDrillDown,
    CoursesMostAbandon, CoursesMostAbandonDrillDown,

    EnrollProgress, EnrollProgressDrillDown,
    EnrollCompleted, EnrollCompletedDrillDown,

    Topics, TopicDrillDown,
    Publishers, PublishersDrillDown,

    UsersMostEnrollments, UsersMostEnrollmentsDrillDown,
    UsersMostCompleted, UsersMostCompletedDrillDown,
    UsersMostAbandon, UsersMostAbandonDrillDown,
    UsersNotAccess, UsersNotAccessDrillDown,
    UsersProgressCourse,

    UserEnrollment,
    Comments,
    Likes
    
}

export interface DashboardCourseAnalysisParams {
    columnToSummary: DashboardCourseAnalysisSummaryColumn;
    detailColumn?: DashboardCourseAnalysisDetailColumn;

    startDate?: Date;
    endDate?: Date;

    topicId?: string;
    includeSubtopics?: boolean;

    authorId?: string;
    courseId?: string;
    memberId?: string;

    selectedValue?: number;

    drillDownFilter?: {
        id: string;
        title?: string;
    };

    range?: PageRange;
}

export interface DashboardCourseAnalysisFilter {
    topicId: string;
    includeSubtopics: boolean;

    authorId: string;
    courseId: string;
    memberId: string;

    includeBlockedUsers: boolean;
}

export interface DashboardCourseAnalysisResumeModel {
    published: number;
    published30: number;
    comments: number;
    comments30: number;
    likes: number;
    likes30: number;
    enrollments: number;
    enrollments30: number;
    completed: number;
    completed30: number;
}


//#endregion


//#region DASHBOARD: COMSUMPTION ANALYSIS

export enum DashboardConsumptionSummaryColumn { Resume, Users, Storage }

export enum DashboardConsumptionDetailColumn { GraphByMonth, ActiveUsers, StorageDistribution }

export interface DashboardConsumptionUserListModel {
    userId: string;
    name: string;
    thumbnailUrl: string;
    lastLogin?: Date;
    lastLoginIpAddress: string;
}

export interface DashboardConsumptionResumeModel {
    totalUsers: number;
    diffUsers: number;
    totalSize: number;
    diffSize: number;
}

export interface DashboardConsumptionStorageModel {
    nonInstitutionalSize: number;
    institutionalSize: number;
    thumbnailsSize: number;
    courseSize: number;
    kwnlTestSize: number;
}

export interface DashboardConsumptionAnalysisParams {
    columnToSummary: DashboardConsumptionSummaryColumn;
    detailColumn?: DashboardConsumptionDetailColumn;
    startDate?: Date;
    endDate?: Date;
    range?: PageRange;
}

export interface DashboardConsumptionResultModel {
    billingType: TenantBillingType;
    maxUsers?: number;
    data: any;

}
//#endregion

//#region DASHBOARD: User and Groups

export interface DashboardKnowledgeTestAnalysisFilter {
    topicId: string;
    topicName?: string;
    topicThumbnailUrl?: string;
}

//#endregion